import type { Messages } from "./en";

/** IT-Partnerportal (/partner, Issue #27). In de.ts unter "partner" eingebunden. */
export const partnerDe: Messages["partner"] = {
  badge: "Partner",
  nav: {
    dashboard: "Übersicht",
    customers: "Kunden",
    billing: "Abrechnung",
    activity: "Aktivität",
    team: "Team",
    account: "Mein Konto",
    signOut: "Abmelden",
  },
  auth: {
    loginTitle: "Partnerportal",
    loginSubtitle: "Die Kontakte all Ihrer Kunden an einem Ort verwalten.",
    email: "E-Mail",
    password: "Passwort",
    passwordHint: "Mindestens 12 Zeichen.",
    passwordConfirm: "Passwort wiederholen",
    passwordMismatch: "Die Passwörter stimmen nicht überein.",
    signIn: "Anmelden",
    passkey: "Mit Passkey anmelden",
    or: "oder",
    forgot: "Passwort vergessen?",
    noAccount: "Noch kein Partnerkonto?",
    register: "Als Partner registrieren",
    customerLogin: "Kunden melden sich mit Microsoft an",
    codeTitle: "Code eingeben",
    codeSubtitle: "Der 6-stellige Code aus Ihrer Authenticator-App.",
    code: "Code",
    backupCode: "Backup-Code",
    useBackup: "Backup-Code verwenden",
    useApp: "Authenticator-App verwenden",
    back: "Zurück",
    registerTitle: "Als Partner registrieren",
    registerSubtitle:
      "Für IT-Partner, die Microsoft 365 für ihre Kunden betreuen. Kostenlos, Sie bezahlen nur für Kunden, die Sie über sich abrechnen.",
    inviteTitle: "{{partner}} beitreten",
    inviteSubtitle: "Erstellen Sie Ihr Konto für das Partnerportal.",
    company: "Firma",
    name: "Ihr Name",
    language: "Sprache",
    create: "Konto erstellen",
    haveAccount: "Bereits registriert?",
    checkEmailTitle: "Prüfen Sie Ihr Postfach",
    checkEmail:
      "Wir haben einen Bestätigungslink an {{email}} gesendet. Nach der Bestätigung richten Sie den zweiten Faktor ein.",
    inviteDone: "Ihr Konto ist bereit. Melden Sie sich jetzt an.",
    forgotTitle: "Passwort zurücksetzen",
    forgotSubtitle: "Wir senden Ihnen einen Link, um ein neues Passwort zu setzen.",
    sendLink: "Link senden",
    linkSent: "Falls es für diese Adresse ein Konto gibt, ist der Link unterwegs.",
    resetTitle: "Neues Passwort setzen",
    resetDone: "Ihr Passwort wurde geändert. Melden Sie sich mit dem neuen Passwort an.",
    save: "Speichern",
    errors: {
      invalid_email_or_password: "E-Mail oder Passwort ist falsch.",
      email_not_verified:
        "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Wir haben Ihnen den Link soeben noch einmal gesendet.",
      invalid_code: "Der Code ist falsch oder abgelaufen.",
      invalid_two_factor_code: "Der Code ist falsch oder abgelaufen.",
      invalid_backup_code: "Dieser Backup-Code ist falsch oder bereits verwendet.",
      company_name_required: "Bitte geben Sie Ihren Firmennamen ein.",
      invalid_invitation:
        "Diese Einladung ist ungültig oder abgelaufen, oder sie wurde an eine andere Adresse gesendet.",
      password_too_short: "Das Passwort braucht mindestens 12 Zeichen.",
      invalid_token: "Der Link ist ungültig oder abgelaufen. Fordern Sie einen neuen an.",
      too_many_requests: "Zu viele Versuche. Warten Sie einen Moment und versuchen Sie es erneut.",
      default: "Das hat nicht geklappt. Bitte versuchen Sie es erneut.",
    },
  },
  secondFactor: {
    requiredTitle: "Zweiten Faktor einrichten",
    required:
      "Partnerkonten erreichen die Daten vieler Kunden. Schützen Sie Ihr Konto mit einer Authenticator-App oder einem Passkey, bevor Sie weiterarbeiten.",
    totpTitle: "Authenticator-App",
    totpText: "Codes aus einer App wie Microsoft Authenticator oder 1Password.",
    totpOn: "Aktiv",
    setUp: "Einrichten",
    confirmPassword: "Mit Ihrem Passwort bestätigen",
    scan: "Scannen Sie den QR-Code mit Ihrer Authenticator-App und geben Sie den aktuellen Code ein.",
    qrAlt: "QR-Code für die Authenticator-App",
    secret: "Oder geben Sie diesen Schlüssel von Hand ein",
    verify: "Bestätigen",
    backupTitle: "Backup-Codes aufbewahren",
    backupText:
      "Jeder Code funktioniert einmal, falls Sie Ihr Telefon verlieren. Bewahren Sie die Codes sicher auf, zum Beispiel im Passwort-Manager.",
    download: "Herunterladen",
    done: "Fertig",
    passkeysTitle: "Passkeys",
    passkeysText:
      "Anmelden mit Fingerabdruck, Gesicht oder Geräte-PIN. Ein Passkey ersetzt Passwort und Code.",
    addPasskey: "Passkey hinzufügen",
    noPasskeys: "Noch kein Passkey.",
    removePasskey: "Entfernen",
    passkeyAdded: "Passkey hinzugefügt.",
    passkeyRemoved: "Passkey entfernt.",
    totpEnabled: "Die Authenticator-App ist eingerichtet.",
  },
  account: {
    title: "Mein Konto",
    profile: "Profil",
    role: "Rolle",
    partnerCode: "Partnercode",
    partnerCodeHint:
      "Ihre Kunden geben diesen Code in Kontaktfix unter Einstellungen, Partner ein, um Ihnen Zugriff zu geben.",
    changePassword: "Passwort ändern",
    currentPassword: "Aktuelles Passwort",
    newPassword: "Neues Passwort",
    passwordChanged: "Passwort geändert. Andere Sitzungen wurden abgemeldet.",
  },
  roles: { admin: "Partner-Admin", technician: "Techniker" },
  customerRoles: { admin: "Administrator", editor: "Listen-Bearbeiter", none: "Kein Zugriff" },
  customers: {
    title: "Kunden",
    description:
      "Jeder Kunde ist ein eigener Microsoft-365-Tenant. Öffnen Sie einen Kunden, um mit Ihrer Rolle darin zu arbeiten.",
    add: "Kunde hinzufügen",
    empty: "Noch keine Kunden",
    emptyText:
      "Fügen Sie Ihren ersten Kunden über seine Microsoft-365-Domain hinzu. Nutzt der Kunde Kontaktfix noch nicht, richten Sie ihn direkt ein.",
    emptyTechnician: "Ihnen sind noch keine Kunden zugewiesen. Ihr Partner-Admin weist Kunden im Team zu.",
    name: "Kunde",
    status: "Status",
    connection: "Microsoft 365",
    myRole: "Ihre Rolle",
    open: "Öffnen",
    pending: "Wartet auf Zustimmung",
    active: "Aktiv",
    connected: "Verbunden",
    notConnected: "Nicht verbunden",
    remove: "Kunde entfernen",
    removeTitle: "{{name}} entfernen?",
    removeText:
      "Ihr Team verliert sofort den Zugriff auf diesen Kunden. Kontakte und Einstellungen des Kunden bleiben, wie sie sind.",
    removed: "Kunde entfernt.",
    addTitle: "Kunde hinzufügen",
    addText:
      "Geben Sie eine Domain des Microsoft-365-Tenants des Kunden ein, zum Beispiel muster.ch oder muster.onmicrosoft.com.",
    domain: "Domain",
    customerName: "Name des Kunden",
    customerLanguage: "Sprache für Mitarbeitende",
    addSubmit: "Hinzufügen",
    addedActive: "Kunde erstellt. Verbinden Sie jetzt Microsoft 365 als Globaler Administrator des Kunden.",
    addedPending:
      "Dieser Kunde nutzt Kontaktfix bereits. Seine Administratoren haben eine Anfrage erhalten, Ihren Zugriff zu bestätigen.",
  },
  team: {
    title: "Team",
    description:
      "Partner-Admins verwalten Kunden, Team und Abrechnung und sind in jedem Kunden Administrator. Techniker arbeiten nur in den Kunden, die Sie ihnen zuweisen.",
    invite: "Einladen",
    inviteTitle: "Ins Team einladen",
    inviteText:
      "Die Person erhält eine E-Mail mit einem Link und erstellt ihr eigenes Konto mit Passwort und zweitem Faktor.",
    invited: "Einladung gesendet.",
    pendingInvitations: "Offene Einladungen",
    expires: "gültig bis {{date}}",
    revoke: "Zurückziehen",
    allCustomers: "Alle Kunden",
    chooseForAll: "Für alle setzen …",
    allChanged_one: "{{count}} Kunde geändert.",
    allChanged_other: "{{count}} Kunden geändert.",
    resend: "Erneut senden",
    resent: "Einladung erneut gesendet. Der neue Link ist 7 Tage gültig.",
    emailLanguage: "Sprache der Einladung",
    member: "Person",
    role: "Rolle",
    secondFactor: "Zweiter Faktor",
    access: "Kundenzugriff",
    accessAll: "Alle Kunden",
    accessCount_one: "{{count}} Kunde",
    accessCount_other: "{{count}} Kunden",
    edit: "Kundenzugriff",
    makeAdmin: "Zum Partner-Admin machen",
    makeTechnician: "Zum Techniker machen",
    remove: "Aus dem Team entfernen",
    removeTitle: "{{name}} entfernen?",
    removeText: "Das Konto wird gelöscht und verliert sofort den Zugriff auf alle Kunden.",
    you: "Sie",
    accessTitle: "Kundenzugriff für {{name}}",
    accessText:
      "Pro Kunde: Administrator, Listen-Bearbeiter oder kein Zugriff. Jede Änderung steht im Verlauf des Kunden.",
    noCustomers: "Noch keine aktiven Kunden.",
  },
  dashboard: {
    title: "Übersicht",
    description: "Alle Ihre Kunden auf einen Blick. Kunden, die Aufmerksamkeit brauchen, stehen zuoberst.",
    customers: "Aktive Kunden",
    pendingCount_one: "{{count}} wartet auf Zustimmung",
    pendingCount_other: "{{count}} warten auf Zustimmung",
    connectedCount_one: "{{count}} mit Microsoft 365 verbunden",
    connectedCount_other: "{{count}} mit Microsoft 365 verbunden",
    people: "Personen",
    peopleHint: "die Kontakte erhalten",
    contacts: "Kontakte",
    monthly: "Über Sie abgerechnet",
    monthlyHint: "pro Monat, ohne MWST",
    attention: "Brauchen Aufmerksamkeit",
    overview: "Kunden",
    attentionCount_one: "{{count}} Kunde braucht Aufmerksamkeit.",
    attentionCount_other: "{{count}} Kunden brauchen Aufmerksamkeit.",
    allGood: "Alles läuft.",
    state: "Status",
    lastSync: "Letzter Abgleich",
    never: "Noch nie",
    problems: {
      ok: "Läuft",
      pending: "Wartet auf Zustimmung",
      billing: "Kein bezahlter Zugang",
      connectionError: "Verbindung unterbrochen",
      notConnected: "Microsoft 365 nicht verbunden",
      syncFailed: "Letzter Abgleich fehlgeschlagen",
    },
  },
  billing: {
    title: "Abrechnung",
    description:
      "Entscheiden Sie pro Kunde, wer bezahlt: der Kunde selbst oder Sie. Für alle Kunden, die über Sie laufen, erhalten Sie eine Rechnung mit jedem Kunden als eigener Position.",
    payment: "Zahlung",
    ready: "Eingerichtet",
    notReady: "Nicht eingerichtet",
    pastDue: "Zahlung fehlgeschlagen",
    notConfigured: "Die Abrechnung ist auf diesem Server nicht eingerichtet.",
    byCard: "Sie zahlen mit Karte. Stripe belastet die Rechnung jeden Monat.",
    noCard:
      "Noch keine Zahlungsmethode. Kunden, die über Sie laufen, haben keinen bezahlten Zugang, bis Sie eine Karte hinterlegen oder auf Rechnung zahlen.",
    byInvoice:
      "Sie zahlen monatlich auf Rechnung, zahlbar innert {{days}} Tagen. Stripe sendet die Rechnung per E-Mail.",
    invoiceRequested: "Ihr Antrag auf Zahlung auf Rechnung wird geprüft.",
    invoiceRejected: "Die Zahlung auf Rechnung wurde nicht freigegeben.",
    pastDueText:
      "Die letzte Zahlung ist fehlgeschlagen. Bitte prüfen Sie Ihre Zahlungsmethode unter Rechnungen und Zahlung.",
    billedCustomers: "Über Sie abgerechnet",
    pricePerUser: "Preis pro Person",
    perUserMonth: "pro Person und Monat",
    total: "Total pro Monat",
    totalHint: "bei der aktuellen Anzahl Personen, ohne MWST",
    addCard: "Karte hinterlegen",
    changeCard: "Karte ändern",
    applyInvoice: "Auf Rechnung zahlen",
    portal: "Rechnungen und Zahlung",
    waitingForStripe: "Wird bei Stripe bestätigt …",
    cardSaved: "Zahlungsmethode gespeichert.",
    customers: "Wer bezahlt",
    customersText:
      "Die Änderung gilt sofort und steht im Verlauf des Kunden. Der Kunde sieht sie auf seiner Abo-Seite.",
    monthly: "Pro Monat",
    billedTo: "Abrechnung",
    toCustomer: "Kunde",
    toPartner: "Sie",
    billedToPartner: "Sie bezahlen",
    changed: "Abrechnung geändert.",
    endsAt: "Sie bezahlen bis am {{date}}, danach der Kunde.",
    ownUntil: "Das eigene Abo des Kunden läuft bis am {{date}}, danach bezahlen Sie.",
    trialUntil: "Testphase bis am {{date}}, danach bezahlen Sie.",
    trialPending: "Die Testphase beginnt mit der ersten Zustellung.",
    customerSubscribed: "Eigenes Abo",
    setAll: "Für alle setzen …",
    allToPartner: "Alle über Sie",
    allToCustomer: "Alle über den Kunden",
    allChanged_one: "Abrechnung für {{count}} Kunde geändert.",
    allChanged_other: "Abrechnung für {{count}} Kunden geändert.",
    reports: "Monatsberichte",
    reportsText:
      "Am 1. jedes Monats, mit allen Kunden, Personen, Kontakten und Beträgen. Partner-Admins erhalten ihn auch per E-Mail.",
    noReports: "Der erste Bericht kommt am 1. des nächsten Monats.",
    reportSummary_one: "{{count}} Kunde, {{total}} pro Monat",
    reportSummary_other: "{{count}} Kunden, {{total}} pro Monat",
    howTitle: "So funktioniert die Partner-Abrechnung",
    howPrice:
      "Gleicher Preis wie für Kunden: CHF 2 pro Person, die Kontakte erhält, und Monat. Jeder Kunde hat seine {{days}}-tägige Testphase; Sie bezahlen ab deren Ende.",
    howSwitch:
      "Wechselt ein Kunde mit eigenem Abo zu Ihnen, endet sein Abo am Ende der bezahlten Periode. Danach bezahlen Sie.",
    howEnd:
      "Stellen Sie einen Kunden zurück oder endet die Verknüpfung, bezahlen Sie bis zum Ende der laufenden Periode. Danach braucht der Kunde ein eigenes Abo.",
    applyTitle: "Zahlung auf Rechnung beantragen",
    applyText:
      "Statt mit Karte zahlen Sie monatlich auf Rechnung, zahlbar innert {{days}} Tagen. Wir prüfen den Antrag und bestätigen ihn per E-Mail.",
    invoiceEmail: "E-Mail für Rechnungen",
    street: "Strasse und Nummer",
    postalCode: "PLZ",
    city: "Ort",
    country: "Land",
    countries: { CH: "Schweiz", LI: "Liechtenstein", DE: "Deutschland", AT: "Österreich" },
    applySubmit: "Beantragen",
    applied: "Antrag gesendet. Wir melden uns per E-Mail.",
  },
  activity: {
    title: "Aktivität",
    description:
      "Was Ihr Team bei Ihren Kunden geändert hat, aus dem Verlauf der Kunden. Jeder Kunde sieht diese Einträge auch selbst.",
    person: "Person",
    everyone: "Ganzes Team",
    empty: "Noch keine Änderungen.",
  },
  banner: {
    working: "Sie arbeiten in {{org}} als {{partner}}.",
    portal: "Partnerportal",
    switch: "Kunde wechseln",
  },
};
